import { useEffect, useState } from "react";
import type { AuthMeResponse } from "@/lib/api/client";

const LATCH_KEY = "hh:new-account";

/**
 * `isNewUser` is only true on the one `/auth/me` response that provisioned the
 * tenant. Every later call returns false — and `useProvisioning` is a cached
 * query that refetches on window focus and after five minutes, so the flag is
 * gone by the second look at the page.
 *
 * That matters because the greeting it drives is the only thing that tells
 * someone who mistyped their address that they are now looking at a brand-new
 * empty account rather than their own (task 212). Losing it would be bad
 * enough; what actually happened without this hook is worse — the message flips
 * to "Welcome back", actively reassuring them.
 *
 * So the flag is latched, per tab, until the account stops being brand new.
 * `isOrgSetupComplete` is that boundary: naming the organisation is the first
 * thing the account holder commits to, and past it "did you mean this address?"
 * is no longer the question. Latching per tab rather than forever means the
 * state cannot outlive the situation it describes.
 */
function readLatch(): string | null {
  try {
    return sessionStorage.getItem(LATCH_KEY);
  } catch {
    // Safari private mode and friends. A missing latch degrades to "only true
    // on the provisioning response", which is where this started.
    return null;
  }
}

function writeLatch(userId: string): void {
  try {
    sessionStorage.setItem(LATCH_KEY, userId);
  } catch {
    /* see readLatch */
  }
}

export function useIsNewAccount(profile: AuthMeResponse | null): boolean {
  const userId = profile?.user.id ?? null;
  const isNewUser = profile?.isNewUser ?? false;
  const [latchedUserId, setLatchedUserId] = useState<string | null>(readLatch);

  useEffect(() => {
    if (!userId) return;
    if (isNewUser) writeLatch(userId);
    setLatchedUserId(readLatch());
  }, [userId, isNewUser]);

  if (!profile || profile.tenant.isOrgSetupComplete) return false;
  return isNewUser || (userId !== null && latchedUserId === userId);
}
